/* eslint-disable react-hooks/refs */

/**
 * Home Component
 *
 * Main page component for the Internet Access Registration application. Handles user authentication,
 * IP address registration, and status tracking across multiple services (EDL, Imperva, Akamai).
 *
 * Features:
 * - Okta authentication integration
 * - IP address detection and geolocation
 * - IP registration API calls
 * - Real-time status polling (every 20 seconds)
 * - Progress tracking across three registration services (EDL, Imperva, Akamai)
 * - Online/offline status detection with auto-refresh on reconnect
 * - Activity logs display and retrieval
 * - Device lock timeout handling with auto-refresh
 * - E1E2 destination form toggle
 * - Responsive mobile/desktop user menu
 * - Slack support channel integration
 *
 * State Management:
 * - dlsMode: Theme mode (currently set to 'dark')
 * - authState: Okta authentication state
 * - ipAddress: User's detected IP address
 * - geoInfo: Geolocation information (country code, location name)
 * - oktaToken: Okta access token for API calls
 * - ipStatus: Current registration status ('checking', 'registering', 'inactive', 'active', 'pending', 'failed', 'apiFailed', 'timeout')
 * - isOnline: Network connectivity status
 * - showE1E2DestinationForm: Toggle for E1E2 destination form visibility
 *
 * Refs:
 * - timerFnRef: Interval reference for polling status checks (20-second intervals)
 * - registrationStatus: Tracks EDL, Imperva, and Akamai service statuses
 * - activityLogs: Stores activity logs from API responses (up to first "Submitted IP for registration" entry)
 * - progressIndicatorValue: Progress percentage (0, 33, 66, or 100)
 * - showProgressIndicator: Controls progress indicator visibility
 * - statusApiTriggeredCount: Counter for status API calls
 * - showMobileUserMenu: Boolean ref for responsive menu state
 * - ipStatusRef: Ref copy of ipStatus for use in intervals
 * - showE1E2DestinationFormBooleanRef: Ref copy for form visibility state
 *
 * API Flows:
 * 1. Collect user's IP address and geolocation on component mount
 * 2. On Okta authentication, retrieve access token
 * 3. Check IP registration status via API
 * 4. If not registered, call registration API to submit IP
 * 5. Poll status API every 20 seconds until registration completes or fails
 * 6. Pause polling when E1E2 destination form is open
 *
 * Status Progression:
 * - pending → active (all 3 services active) - progress: 100%
 * - pending → inactive (all or some services inactive) - progress: 0%
 * - pending → failed (any service failed) - progress: 0%
 * - 2 out of 3 services active - progress: 66%
 * - 1 out of 3 services active - progress: 33%
 * - All pending - progress: 0%
 *
 * Error Handling:
 * - Network/unknown errors: 'apiFailed' status
 * - Timeout errors: 'timeout' status
 * - Phone locked timeout: Auto-refresh after 500ms
 * - Offline: Stop polling, display offline message
 * - Online: Auto-refresh page after 2 seconds
 */

import { useOktaAuth } from '@okta/okta-react'
import { useState, useEffect, useRef } from 'react'
import { Button, Flag, ProgressCircular, Surface } from '@americanexpress/dls-react'
import { IconSuccess, IconWarning } from '@americanexpress/dls-icons'
import { useOutletContext } from 'react-router-dom'

import styles from '@/scss/Home.module.scss'
import { getAPIServerByENV } from '@/utilityFunctions.js'
import AppLogo from './common/AppLogo'
import useFaviconSwitcher from './common/hooks/useFaviconSwitcher'
import ProgressIndicatorWrapper from './ProgressIndicatorWrapper'
import RegistrationMultiStepTracker from './RegistrationMultiStepTracker'
import ActivityLogsModal from './ActivityLogsModal'
import Login from './Login'
import UserMenuDesktop from './UserMenuDesktop'
import UserMenuMobile from './UserMenuMobile'
import E1E2DestinationForm from './E1E2DestinationForm'

const mobileUserMenuBreakpoint = 1112 // px

const Home = () => {
  const [dlsMode] = useState('dark') // optons are `light`, `dark`, or `system`
  const { authState, oktaAuth } = useOktaAuth()
  const [, setUserInfo] = useState(null)
  const [ipAddress, setIpAddress] = useState(null)
  const [geoInfo, setGeoInfo] = useState(null)
  const [oktaToken, setOktaToken] = useState(null)
  const [ipStatus, setIpStatus] = useState(null)
  const [isOnline, setOnlineBoolean] = useState(navigator.onLine)
  const [showE1E2DestinationForm, toggleE1E2DestinationForm] = useState(false)
  const [, toggleRender] = useState(0)

  const containerRef = useRef(null)
  const showMobileUserMenu = useRef(window.innerWidth <= mobileUserMenuBreakpoint)
  const timerFnRef = useRef(() => {})
  const registrationStatus = useRef({
    EDL: '',
    Imperva: '',
    Akamai: '',
  })
  const activityLogs = useRef([])
  const ipStatusRef = useRef(ipStatus)
  ipStatusRef.current = ipStatus

  const progressIndicatorValue = useRef(0)
  const showProgressIndicator = useRef(false)
  const statusApiTriggeredCount = useRef(0)
  const showE1E2DestinationFormBooleanRef = useRef(false)
  showE1E2DestinationFormBooleanRef.current = showE1E2DestinationForm

  const { lockAwareFetch } = useOutletContext()

  useFaviconSwitcher(registrationStatus.current)

  const httpsMode = import.meta.env.VITE_HTTPS_MODE === 'true'
  const SERVER_PORT = import.meta.env.VITE_SERVER_PORT || 8080

  getAPIServerByENV()
  const iarApiUrl = window?.env?.VITE_IAR_API
  const ipUrl = window.location.hostname.includes('localhost')
    ? `http${httpsMode ? 's' : ''}://localhost.aexp.com:${SERVER_PORT}/ip`
    : '/ip'

  // collect User's IP address
  useEffect(() => {
    fetch(ipUrl)
      .then(response => response.json())
      .then(data => {
        setIpAddress(data.ip)
        if (data.geo) setGeoInfo(data.geo)
      })
      .catch(error => {
        console.error('Error fetching IP address:', error)
      })

    const handleOnline = () => {
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    }
    const handleOffline = () => {
      clearInterval(timerFnRef.current)
      setOnlineBoolean(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      const width = entry.contentRect.width

      if (width <= mobileUserMenuBreakpoint && !showMobileUserMenu.current) {
        showMobileUserMenu.current = true
        toggleRender(prev => prev + 1) // Force re-render to show mobile menu
      } else if (width > mobileUserMenuBreakpoint && showMobileUserMenu.current) {
        showMobileUserMenu.current = false
        toggleRender(prev => prev + 1) // Force re-render to hide mobile menu
      }
    })

    setTimeout(() => {
      if (containerRef.current) {
        observer.observe(containerRef.current)
      }
    }, 500)

    return () => {
      clearInterval(timerFnRef.current)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!authState || !authState.isAuthenticated) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUserInfo(null)
    } else {
      oktaAuth?.getUser().then(info => {
        setUserInfo(info)
      })
      setOktaToken(oktaAuth.getAccessToken())
    }
  }, [authState, oktaAuth])

  const login = async () => {
    await oktaAuth.signInWithRedirect()
  }

  const logout = async () => {
    await oktaAuth.signOut()
  }

  const refreshWhenDeviceLockedForSoLong = () => {
    setTimeout(() => {
      window.location.reload()
    }, 500)
  }

  /**
   * Call the registration API
   * @param {String} url - API URL defined in the .env file
   * @param {String} token - Okta access token
   * @param {String} ip - client's IP address
   */
  const callRegistrationApi = async (url, ip) => {
    const {
      accessToken: { accessToken: token },
    } = await oktaAuth.tokenManager.getTokens()

    if (!showProgressIndicator.current) {
      showProgressIndicator.current = true
      progressIndicatorValue.current = 0
    }
    setIpStatus('registering')
    lockAwareFetch(`${url}/api/internet-registration`, {
      method: 'POST',
      headers: {
        'true-client-ip': `${ip}`,
        location: geoInfo?.country || 'Unknown',
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
      .then(response => {
        if (response.success) {
          checkIPStatus(url, ip)
        } else if (!response.success && response?.errorInfo?.message) {
          showProgressIndicator.current = false
          progressIndicatorValue.current = 0
          setIpStatus('failed')
        } else if (!response.success && response?.message === 'Unable to register IP address') {
          showProgressIndicator.current = false
          progressIndicatorValue.current = 0
          setIpStatus('apiFailed')
        }
      })
      .catch(error => {
        console.error('Error during registration:', error)
        if (error.kind === 'network' || error.kind === 'unknown') {
          showProgressIndicator.current = false
          progressIndicatorValue.current = 0
          setIpStatus('apiFailed')
        } else if (error.kind === 'timeout') {
          showProgressIndicator.current = false
          progressIndicatorValue.current = 0
          setIpStatus('timeout')
        } else if (error.kind === 'phone-locked-timeout') {
          refreshWhenDeviceLockedForSoLong()
        }
      })
  }

  const checkIPStatus = async (url, ip) => {
    clearInterval(timerFnRef.current)

    const {
      accessToken: { accessToken: token },
    } = await oktaAuth.tokenManager.getTokens()

    if (!showProgressIndicator.current) {
      showProgressIndicator.current = true
      progressIndicatorValue.current = 0
    }

    statusApiTriggeredCount.current += 1

    setIpStatus('checking')
    lockAwareFetch(`${url}/api/ip-registration-status`, {
      method: 'GET',
      headers: {
        'true-client-ip': ip,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
      .then(response => {
        if (!response.success && response?.errorInfo?.message === 'IP is not registered') {
          callRegistrationApi(url, ip)
        } else if (response.errorCode) {
          showProgressIndicator.current = false
          progressIndicatorValue.current = 0
          setIpStatus('apiFailed')
        } else if (response.success) {
          registrationStatus.current = {
            EDL: response.data.edlStatus,
            Imperva: response.data.impervaStatus,
            Akamai: response.data.akamaiStatus,
          }

          if (response.data.activityLogs.length > 0) {
            const firstOccIdx = response.data.activityLogs.findIndex(
              log => log.message === 'Submitted IP for registration'
            )

            if (firstOccIdx > -1) {
              const logs = response.data.activityLogs.slice(0, firstOccIdx + 1)
              activityLogs.current = logs
            } else {
              activityLogs.current = response.data.activityLogs
            }
          } else {
            activityLogs.current = []
          }

          if (
            (response.data.akamaiStatus === 'inactive' &&
              response.data.edlStatus === 'inactive' &&
              response.data.impervaStatus === 'inactive') ||
            response.data.edlStatus === 'inactive' ||
            response.data.edlStatus === 'failed'
          ) {
            showProgressIndicator.current = false
            progressIndicatorValue.current = 0
            setIpStatus('inactive')
          } else if (
            response.data.akamaiStatus === 'active' &&
            response.data.edlStatus === 'active' &&
            response.data.impervaStatus === 'active'
          ) {
            showProgressIndicator.current = true
            progressIndicatorValue.current = 100
            setIpStatus('active')
          } else if (
            ((!response.data.akamaiStatus || response.data.akamaiStatus !== 'active') &&
              response.data.impervaStatus === 'active' &&
              response.data.edlStatus === 'active') ||
            ((!response.data.impervaStatus || response.data.impervaStatus !== 'active') &&
              response.data.akamaiStatus === 'active' &&
              response.data.edlStatus === 'active') ||
            ((!response.data.edlStatus || response.data.edlStatus !== 'active') &&
              response.data.akamaiStatus === 'active' &&
              response.data.impervaStatus === 'active')
          ) {
            showProgressIndicator.current = true
            progressIndicatorValue.current = 66
            setIpStatus('pending')
          } else if (
            (response.data.akamaiStatus === 'active' &&
              (!response.data.edlStatus ||
                response.data.edlStatus !== 'active' ||
                !response.data.impervaStatus ||
                response.data.impervaStatus !== 'active')) ||
            (response.data.edlStatus === 'active' &&
              (!response.data.akamaiStatus ||
                response.data.akamaiStatus !== 'active' ||
                !response.data.impervaStatus ||
                response.data.impervaStatus !== 'active')) ||
            (response.data.impervaStatus === 'active' &&
              (!response.data.akamaiStatus ||
                response.data.akamaiStatus !== 'active' ||
                !response.data.edlStatus ||
                response.data.edlStatus !== 'active'))
          ) {
            showProgressIndicator.current = true
            progressIndicatorValue.current = 33
            setIpStatus('pending')
          } else if (
            response.data.akamaiStatus === 'pending' &&
            response.data.edlStatus === 'pending' &&
            response.data.impervaStatus === 'pending'
          ) {
            showProgressIndicator.current = true
            progressIndicatorValue.current = 0
            setIpStatus('pending')
          } else if (
            response.data.akamaiStatus === 'failed' ||
            response.data.edlStatus === 'failed' ||
            response.data.impervaStatus === 'failed'
          ) {
            showProgressIndicator.current = false
            progressIndicatorValue.current = 0
            setIpStatus('failed')
          }

          if (!showE1E2DestinationFormBooleanRef.current) {
            timerFnRef.current = setInterval(() => {
              if (
                ipStatusRef.current !== 'inactive' &&
                ipStatusRef.current !== 'failed' &&
                ipStatusRef.current !== 'apiFailed'
              ) {
                checkIPStatus(url, ip)
              }
            }, 20000)
          }
        } else if (
          !response.success &&
          response?.message === 'Unable to retrieve IP registration status'
        ) {
          showProgressIndicator.current = false
          progressIndicatorValue.current = 0
          setIpStatus('apiFailed')
        }
      })
      .catch(error => {
        console.error('Error during status check:', error)
        if (error.kind === 'network' || error.kind === 'unknown') {
          showProgressIndicator.current = false
          progressIndicatorValue.current = 0
          setIpStatus('apiFailed')
        } else if (error.kind === 'timeout') {
          showProgressIndicator.current = false
          progressIndicatorValue.current = 0
          setIpStatus('timeout')
        } else if (error.kind === 'phone-locked-timeout') {
          refreshWhenDeviceLockedForSoLong()
        }
      })
  }

  const reRegisterIP = () => {
    if ((iarApiUrl || iarApiUrl === '') && ipAddress) {
      callRegistrationApi(iarApiUrl, ipAddress)
    }
  }

  useEffect(() => {
    if ((iarApiUrl || iarApiUrl === '') && ipAddress && oktaToken) {
      checkIPStatus(iarApiUrl, ipAddress)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oktaToken, ipAddress, iarApiUrl])

  const switchE1E2DestinationForm = () => {
    const open = !showE1E2DestinationForm

    if (open) {
      clearInterval(timerFnRef.current)
      toggleE1E2DestinationForm(true)
    } else {
      toggleE1E2DestinationForm(false)
      if ((iarApiUrl || iarApiUrl === '') && ipAddress && oktaToken) {
        checkIPStatus(iarApiUrl, ipAddress)
      }
    }
  }

  const getProgressValueAndStatusApiCallCount = () => ({
    progressIndicatorValue: progressIndicatorValue.current,
    statusApiTriggeredCount: statusApiTriggeredCount.current,
  })

  const getSlackChannelUrl = () => (
    <a
      className={styles.slackChannelLink}
      href={window.env.netSecSupportSlackUrl}
      target='_blank'
      rel='noreferrer'
    >
      #net-sec-support
    </a>
  )

  if (!authState) {
    return (
      <div className={styles.loadingWrapper}>
        <ProgressCircular size='lg' />
      </div>
    )
  }

  return (
    <Surface
      ref={containerRef}
      variant='base'
      data-dls-mode={dlsMode}
      className={`header text-align-center ${styles.wrapper}`}
    >
      <div>
        {authState?.isAuthenticated && (
          <>
            <AppLogo
              isRegistrationCompleted={
                registrationStatus.current.EDL === 'active' &&
                registrationStatus.current.Imperva === 'active' &&
                registrationStatus.current.Akamai === 'active'
              }
            />
            <br />
          </>
        )}

        {!authState?.isAuthenticated && <Login loginOnclick={login} />}

        {!isOnline && (
          <>
            <div className={styles.onlineStatus}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                stroke-width='2'
                stroke-linecap='round'
                stroke-linejoin='round'
                class='lucide lucide-wifi-off-icon lucide-wifi-off'
              >
                <path d='M12 20h.01' />
                <path d='M8.5 16.429a5 5 0 0 1 7 0' />
                <path d='M5 12.859a10 10 0 0 1 5.17-2.69' />
                <path d='M19 12.859a10 10 0 0 0-2.007-1.523' />
                <path d='M2 8.82a15 15 0 0 1 4.177-2.643' />
                <path d='M22 8.82a15 15 0 0 0-11.288-3.764' />
                <path d='m2 2 20 20' />
              </svg>
              You are offline.
            </div>
            <p style={{ marginTop: '1rem' }}>Please check your internet connection.</p>
          </>
        )}

        {authState?.isAuthenticated && isOnline && !showE1E2DestinationForm && (
          <>
            <div>
              <ProgressIndicatorWrapper
                showProgressIndicator={showProgressIndicator.current}
                ipStatus={ipStatus}
                getProgressValueAndStatusApiCallCount={getProgressValueAndStatusApiCallCount}
              />
            </div>
            <div>
              {showProgressIndicator.current && (
                <RegistrationMultiStepTracker registrationStatus={registrationStatus.current} />
              )}
            </div>
            <div>
              {ipStatus === 'checking' && <>Checking your IP registration status . . .</>}
              {ipStatus === 'registering' && <>Submitting your IP address for registration . . .</>}
              {ipStatus === 'inactive' && (
                <>
                  Your IP address is registered but inactive.
                  <p style={{ marginTop: '1rem' }}>
                    Click the Re-register button to activate your IP.
                  </p>
                </>
              )}
              {ipStatus === 'active' && (
                <>
                  Your IP address is registered and active.{' '}
                  <IconSuccess color='success' size='lg' isFilled={true} />{' '}
                </>
              )}
              {ipStatus === 'pending' && (
                <>
                  Your IP address registration is in progress.
                  <p style={{ marginTop: '1rem' }}>
                    Please note this may take up to 8 - 10 minutes.
                  </p>
                  <p style={{ marginTop: '1rem' }}>This page will auto-refresh every 20 seconds.</p>
                  <p style={{ marginTop: '1rem' }}>Thank you for your patience.</p>
                </>
              )}
              {ipStatus === 'failed' && (
                <>
                  Your IP address registration has failed.{' '}
                  <IconWarning color='critical' size='lg' isFilled={true} />{' '}
                  <p style={{ marginTop: '1rem' }}>
                    Please contact the administrator on Slack at {getSlackChannelUrl()} for
                    assistance.
                  </p>{' '}
                </>
              )}
              {ipStatus === 'apiFailed' && (
                <>
                  Something went wrong while connecting to our servers.
                  <p style={{ marginTop: '1rem' }}>
                    Your IP address registration has failed.
                    <IconWarning
                      style={{ marginLeft: '0.4rem' }}
                      color='critical'
                      size='lg'
                      isFilled={true}
                    />{' '}
                  </p>
                  <p style={{ marginTop: '1rem' }}>
                    Please contact the administrator on Slack at {getSlackChannelUrl()} for
                    assistance.
                  </p>
                  <p style={{ marginTop: '1rem' }}>
                    Alternatively, refresh the page and try again after some time.
                  </p>
                </>
              )}
              {ipStatus === 'timeout' && (
                <>
                  Request timed out. <IconWarning color='critical' size='lg' isFilled={true} />{' '}
                  <p style={{ marginTop: '1rem' }}>
                    Please check your connection, refresh the page, and try again.
                  </p>
                  <p style={{ marginTop: '1rem' }}>
                    If the issue persists, contact the administrator on Slack at{' '}
                    {getSlackChannelUrl()}.
                  </p>
                </>
              )}
              <p style={{ marginTop: '1rem' }}>
                IP Address: {ipAddress || 'Getting IP...'}
                <ActivityLogsModal activityLogs={activityLogs.current} />
              </p>
              {geoInfo && (
                <div className={styles.geoInfoWrapper}>
                  <Flag countryCode={geoInfo.country || ''} size='sm' />
                  {geoInfo.name ? `(${geoInfo.name})` : ''}
                </div>
              )}
            </div>
            {ipStatus === 'inactive' && (
              <Button style={{ margin: '1.3rem 0rem' }} onClick={reRegisterIP}>
                Re-register
              </Button>
            )}
          </>
        )}

        {authState?.isAuthenticated && isOnline && showE1E2DestinationForm && (
          <E1E2DestinationForm switchE1E2DestinationForm={switchE1E2DestinationForm} />
        )}

        {authState?.isAuthenticated && isOnline && (
          <>
            {showMobileUserMenu.current ? (
              <UserMenuMobile
                switchE1E2DestinationForm={switchE1E2DestinationForm}
                logout={logout}
              />
            ) : (
              <UserMenuDesktop
                switchE1E2DestinationForm={switchE1E2DestinationForm}
                logout={logout}
              />
            )}
          </>
        )}
      </div>

      {authState?.isAuthenticated && (
        <div className={styles.copyrightTextWrapper}>
          <p>
            {`\u00A9 ${new Date().getFullYear()} American Express Company. All Rights Reserved`}
          </p>
        </div>
      )}
    </Surface>
  )
}

export default Home
